import type { EntityConfigDefinition, DisplayColumnConfig, FilterConfig } from '../../types/entityConfig'
import { TABLE_STEP_TYPE } from '../../utils/displayFormatters'

const config: EntityConfigDefinition = {
  name: 'SERVICE_EXPR_MAPPING',
  idField: 'SERVICE_EXPR_MAPPING_ID',
  
  // Use optimized query for filtering
  useOptimizedQuery: true,
  optimizedQueryName: 'getFilteredServiceExprMappings',
  supportsPagination: true,
  
  // Form processing configuration
  keepAuditFieldsOnUpdate: false,
  
  // Environment copy configuration
  preserveOnCopy: ['SERVICE_EXPR_MAPPING_ID'],
  
  // Data loading configuration
  loadProductOptions: true,
  loadServiceOptions: true,
  requiresServiceParamMappings: true,
  
  // Integrated service selection for parameter filtering
  integratedFilters: {
    SERVICE_PARAM_MAPPING_ID: {
      filterField: 'PRODUCT_ID',
      filterLabel: 'Product',
      filterSource: 'ORIGIN_PRODUCT',
      filterValueField: 'ORIGIN_PRODUCT_ID',
      filterDisplayField: 'PRODUCT_ID'
    },
    SOURCE_SERVICE_PARAM_ID: {
      filterField: 'SOURCE_SERVICE_ID',
      filterLabel: 'Source Service',
      filterSource: 'SERVICE',
      filterValueField: 'SERVICE_ID',
      filterDisplayField: 'URI'
    },
    TARGET_SERVICE_PARAM_ID: {
      filterField: 'TARGET_SERVICE_ID', 
      filterLabel: 'Target Service',
      filterSource: 'SERVICE',
      filterValueField: 'SERVICE_ID',
      filterDisplayField: 'URI'
    }
  },
  
  // Fields to remove before GraphQL mutations
  fieldsToRemove: [
    'ORIGIN_PRODUCT_ID', 
    'SOURCE_SERVICE_PARAM_ID', 
    'SOURCE_PARAM_NAME', 
    'TARGET_SERVICE_PARAM_ID', 
    'TARGET_PARAM_NAME',
    'ORIGIN_PRODUCT_DISPLAY',
    'SERVICE_PARAM_MAPPING_DISPLAY',
    'SERVICE_PARAM_MAPPING_ID_DISPLAY',
    'SOURCE_SERVICE_DISPLAY',
    'TARGET_SERVICE_DISPLAY',
    'STEP_TYPE_NAME',
    'CREATED_BY_USER_ID',
    'CREATED_DATE'
  ],
  
  // Display field mapping for non-standard naming
  displayFieldMapping: {
    SERVICE_PARAM_MAPPING_ID: 'SERVICE_PARAM_MAPPING_DISPLAY'
  },
  
  // Foreign key relationships for dropdown options
  foreignKeys: {
    SERVICE_PARAM_MAPPING_ID: {
      table: 'SERVICE_PARAM_MAPPING',
      valueField: 'SERVICE_PARAM_MAPPING_ID',
      displayField: 'SERVICE_PARAM_MAPPING_DISPLAY',
      displayFormat: '{SOURCE_SERVICE_PARAM_ID}: {SOURCE_PARAM_NAME} > {TARGET_SERVICE_PARAM_ID}: {TARGET_PARAM_NAME}'
    },
    STEP_TYPE_ID: {
      table: 'STEP_TYPE',
      valueField: 'STEP_TYPE_ID',
      displayField: 'STEP_TYPE_NAME'
    }
  },
  
  // Field lookups for display enhancement
  fieldLookups: {
    SERVICE_PARAM_MAPPING_ID: {
      lookupTable: 'SERVICE_PARAM_MAPPING',
      foreignKey: 'SERVICE_PARAM_MAPPING_ID',
      displayField: 'SERVICE_PARAM_MAPPING_DISPLAY',
      replaceOriginal: true
    },
    STEP_TYPE_ID: {
      lookupTable: 'STEP_TYPE',
      foreignKey: 'STEP_TYPE_ID',
      displayField: 'STEP_TYPE_NAME',
      displayFormat: TABLE_STEP_TYPE,
      replaceOriginal: true
    }
  },
  
  // Auto-create missing foreign key records
  autoCreateForeignKeys: {
    STEP_TYPE_ID: {
      entity: 'STEP_TYPE',
      matchFields: ['STEP_TYPE_NAME'],
      copyFields: ['STEP_TYPE_NAME', 'STEP_TYPE_DESC', 'RESOURCE_NAME']
    }
  },
  
  // Filter configuration
  hasFilters: true,
  requiresProductFilter: true,
  showEmptyUntilFiltered: true,
  requiresTargetServiceFilter: false,
  
  // Filter configuration for product selection
  filterType: 'product',
  
  // Custom filters for this entity
  customFilters: {
    pscuClient: {
      label: 'Filter by PSCU Client ID',
      field: 'PSCU_CLIENT_ID',
      type: 'pscuClient',
      required: false,
      showAlways: true,
      order: 1
    },
    product: {
      label: 'Filter by Product',
      field: 'ORIGIN_PRODUCT_ID',
      type: 'product',
      required: true,
      showAlways: true,
      order: 2
    },
    sourceService: {
      label: 'Filter by Source Service',
      field: 'SOURCE_SERVICE_PARAM_ID',
      type: 'service',
      required: false,
      showAlways: true,
      order: 3
    },
    targetService: {
      label: 'Filter by Target Service',
      field: 'TARGET_SERVICE_PARAM_ID',
      type: 'service',
      required: false,
      showAlways: true,
      order: 4
    }
  },
  
  // Auto-fill form from filter selection
  autoFillFromFilter: {
    filterField: 'selectedProductFilter',
    formField: 'ORIGIN_PRODUCT_ID'
  },
  
  // Modal pre-population configuration
  modalPrePopulation: {
    productFilter: 'ORIGIN_PRODUCT_ID',
    sourceServiceFilter: 'SOURCE_SERVICE_ID',
    targetServiceFilter: 'TARGET_SERVICE_ID'
  },
  
  // UI configuration
  dateFieldPattern: 'DATE',
  filterChangeHandler: 'filterServiceParamMappings',
  buttonText: {
    create: 'Add New',
    viewOnly: 'View Only Mode'
  },
  emptyStateMessages: {
    requiresProductFilter: 'Please select a product from the filter above to view SERVICE_EXPR_MAPPING records.',
    default: 'No SERVICE_EXPR_MAPPING records found.'
  },
  
  // Environment comparison configuration
  comparisonConfig: {
    matchingFields: ['ORIGIN_PRODUCT_ID', 'SOURCE_SERVICE_PARAM_ID', 'TARGET_SERVICE_PARAM_ID'],
    comparisonFields: ['ORIGIN_PRODUCT_ID', 'SOURCE_SERVICE_PARAM_ID', 'TARGET_SERVICE_PARAM_ID'],
    useDisplayValues: {
      'ORIGIN_PRODUCT_ID': 'PRODUCT_ID',
      'SOURCE_SERVICE_PARAM_ID': 'PARAM_NAME',
      'TARGET_SERVICE_PARAM_ID': 'PARAM_NAME'
    },
    useEnhancedValues: true,
    requiresProductFilter: true,
    showEmptyUntilFiltered: true,
    productFilterField: 'ORIGIN_PRODUCT_ID',
    productFilterMapping: {
      sourceField: 'PRODUCT_ID',
      targetField: 'ORIGIN_PRODUCT_ID'
    },
    displayFieldMapping: {
      ORIGIN_PRODUCT_ID: 'PRODUCT_ID',
      SOURCE_SERVICE_PARAM_ID: 'PARAM_NAME',
      TARGET_SERVICE_PARAM_ID: 'PARAM_NAME'
    },
    displayFields: ['SERVICE_EXPR_MAPPING_ID', 'SERVICE_PARAM_MAPPING_ID', 'SOURCE_EXPR', 'TARGET_EXPR', 'COMMENT_TEXT', 'CREATED_BY_USER_ID', 'CREATED_DATE', 'CHANGED_BY_USER_ID', 'CHANGED_DATE'],
    resolveLookupsForBothEnvironments: true
  },
  
  // Display columns with foreign key lookups
  displayColumns: [
    {
      field: 'SERVICE_PARAM_MAPPING_ID',
      lookup: {
        table: 'SERVICE_PARAM_MAPPING',
        foreignKey: 'SERVICE_PARAM_MAPPING_ID',
        displayField: 'SERVICE_PARAM_MAPPING_DISPLAY'
      }
    },
    {
      field: 'STEP_TYPE_ID',
      lookup: {
        table: 'STEP_TYPE',
        foreignKey: 'STEP_TYPE_ID',
        displayField: 'STEP_TYPE_NAME'
      }
    }
  ] as DisplayColumnConfig[],

  // Unified filter configuration
  filters: {
    table: [
      { type: 'custom', field: 'PSCU_CLIENT_ID', label: 'Filter by PSCU Client ID' },
      { type: 'product', field: 'ORIGIN_PRODUCT_ID', label: 'Filter by Product', required: true },
      { type: 'service', field: 'SOURCE_SERVICE_PARAM_ID', label: 'Filter by Source Service' },
      { type: 'service', field: 'TARGET_SERVICE_PARAM_ID', label: 'Filter by Target Service' }
    ] as FilterConfig[],
    modal: [
      { type: 'integrated', field: 'PRODUCT_ID', label: 'Product', source: 'ORIGIN_PRODUCT' },
      { type: 'integrated', field: 'SOURCE_SERVICE_ID', label: 'Source Service', source: 'SERVICE' },
      { type: 'integrated', field: 'TARGET_SERVICE_ID', label: 'Target Service', source: 'SERVICE' }
    ] as FilterConfig[],
    requiresProduct: true,
    showEmptyUntilFiltered: true
  },

  // Fields configuration
  fields: [
    "SERVICE_EXPR_MAPPING_ID",
    "SERVICE_PARAM_MAPPING_ID",
    "SOURCE_EXPR",
    "TARGET_EXPR",
    "COMMENT_TEXT",
    "CREATED_BY_USER_ID",
    "CREATED_DATE",
    "CHANGED_BY_USER_ID",
    "CHANGED_DATE"
  ],
  
  // Form fields configuration
  formFields: [
    {
      "name": "SERVICE_PARAM_MAPPING_ID",
      "type": "select",
      "required": true,
      "disabled": false
    },
    {
      "name": "STEP_TYPE_ID",
      "type": "select",
      "required": false,
      "disabled": false
    },
    {
      "name": "SEQUENCE_NBR",
      "type": "number",
      "required": false,
      "disabled": false
    },
    {
      "name": "SOURCE_EXPR",
      "type": "text",
      "required": false,
      "disabled": false
    },
    {
      "name": "TARGET_EXPR",
      "type": "text",
      "required": false,
      "disabled": false
    },
    {
      "name": "COMMENT_TEXT",
      "type": "text",
      "required": false,
      "disabled": false
    },
    {
      "name": "CREATED_BY_USER_ID",
      "type": "number",
      "required": false,
      "disabled": false
    },
    {
      "name": "CREATED_DATE",
      "type": "text",
      "required": false,
      "disabled": true
    },
    {
      "name": "CHANGED_BY_USER_ID",
      "type": "number",
      "required": false,
      "disabled": false
    },
    {
      "name": "CHANGED_DATE",
      "type": "text",
      "required": false,
      "disabled": true
    }
  ]
}

export default config

